import type { 
    SubscriptionSeats, 
    ClientInApp,
    PaddlePriceIds 
} from "../types/SubscriptionTypes";

/**
 * Shared service for calculating purchased seats from subscription items
 * Can be used by both client-side and server-side code
 */
export class SubscriptionSeatsService {
    
    /**
     * Calculate purchased seats from parsed client data
     * @param client - The parsed client object with subscription details
     * @param priceIds - Price IDs for different seat types (from env vars)
     * @returns SubscriptionSeats - Number of purchased user and location seats
     */
    static getPurchasedSeats(client: ClientInApp, priceIds: PaddlePriceIds): SubscriptionSeats {
        if (!client.subscription_details) {
            return { userSeats: 0, locationSlots: 0 };
        }

        try {
            // Calculate from subscription items
            // Guard against corrupted DB rows where items was stored as {"0": {...}} instead of an array
            const rawItems = client.subscription_details.items as unknown;
            const items = Array.isArray(rawItems)
                ? rawItems
                : Object.values(rawItems ?? {});

            let userSeats = 0;
            let locationSlots = 0;
            
            for (const item of items) {
                const priceId = item.price_id;

                if (priceId === priceIds.PADDLE_STD_USER_MONTHLY_ID ||
                    priceId === priceIds.PADDLE_STD_USER_YEARLY_ID) {
                    userSeats += item.quantity || 0;
                } else if (priceId === priceIds.PADDLE_STD_LOCATION_MONTHLY_ID ||
                    priceId === priceIds.PADDLE_STD_LOCATION_YEARLY_ID) {
                    locationSlots += item.quantity || 0;
                } else {
                    // price_id may not match when subscription data was created in a different
                    // Paddle environment (e.g. prod data on stage). Fall back to price_description
                    // which is a stable semantic field we write ourselves.
                    const desc = item.price_description ?? '';
                    if (desc.startsWith('user_seat')) {
                        userSeats += item.quantity || 0;
                    } else if (desc.startsWith('location_seat')) {
                        locationSlots += item.quantity || 0;
                    }
                }
            }
            
            return { userSeats, locationSlots };
        } catch (error) {
            console.error('Error calculating seats from subscription details:', error);
            return { userSeats: 0, locationSlots: 0 };
        }
    }
}
